import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useSession } from '@/features/auth/api/useSession'
import { api } from '@/lib/api'
import { CAP, canAll } from '@/lib/can'
import { queryKeys } from '@/lib/query'
import { normaliseDecimal } from '@/features/finance/lines'
import { partyPayload } from '@/features/finance/paymentParty'
import { useCaseInvoices } from '@/features/finance/api/useInvoices'
import { useCaseDebitNotes } from '@/features/finance/api/useDebitNotes'
import {
  MOCK_AGING,
  MOCK_APPROVALS,
  MOCK_BALANCES,
  MOCK_ISSUED_DOCUMENTS,
  MOCK_PAYMENTS,
  MOCK_TRANCHES,
  MOCK_UNINVOICED,
} from '@/mocks/finance'
import { USE_MOCKS } from '@/mocks/fixtures'
import { MOCK_ENTITIES } from '@/mocks/directories'
import {
  toCaseDocumentFromDebitNote,
  toCaseDocumentFromInvoice,
} from '@/types/domain'
import type { AllocationSplitDraft } from '@/features/finance/components/PaymentAllocationSheet'
import type { PaymentDraft } from '@/features/finance/components/FinanceFormSheets'
import type { NewInvoicePayload } from '@/features/finance/invoice'
import type {
  AgingBucket,
  Allocation,
  AllocationPayload,
  ApprovalRequest,
  CaseDocument,
  CasePaymentPayload,
  CaseTrancheRow,
  CounterpartyBalance,
  CurrentUser,
  DebitNote,
  DebitNotePayload,
  ExpenseLine,
  Invoice,
  InvoiceSummary,
  IssuedDocumentsPage,
  Payment,
  PaymentMethod,
  PaymentPayload,
  PaymentStatus,
  UnallocatedOutbound,
  UninvoicedReceivable,
} from '@/types/domain'

/* Finance holds AR/AP aging, counterparty balances, payments, rate-change
 * approvals, and invoices and debit notes. All five are filterable by date
 * range (FR-53) and the range control is SHARED, not reinvented per view —
 * which is why the range is a page-level param threaded into each hook rather
 * than local state inside each panel. */

/** The one side the aging screen reads (issue #125, N-03).
 *
 *  The screen is «Дебиторка по срокам» — what CLIENTS owe — and the server
 *  answers BOTH sides when `side` is absent (`AgingQuery.Side == ""`). Sent
 *  without it, the buckets summed carriers' payables into the receivables: on
 *  the stand 8 300.00 of a 29 014.95 USD total was money we owe, and the
 *  receivable figure no longer matched the dashboard's «К получению». AR and AP
 *  are never summed into one bucket; a payables aging would be its own request
 *  with `side: 'payable'` — and its own query key, since this one does not
 *  carry the side. */
const AGING_SIDE = 'receivable'

export function useAging(range: string) {
  return useQuery<AgingBucket[]>({
    queryKey: queryKeys.finance.aging(range),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_AGING
        : (
            await api.get<{ items: AgingBucket[] }>('/finance/aging', {
              params: { range, side: AGING_SIDE },
            })
          ).items,
  })
}

/** `GET /finance/balances` as a whole (FR-35, S-12, N-04): the per-counterparty
 *  rows, and — at the top level — the outbound payments nobody has allocated
 *  and the receivables the rows leave out.
 *
 *  `unallocatedOutbound` is ALL-TIME and independent of `range` and side
 *  (S-12), served even when `items` are withheld for a caller without vendor
 *  identity (it names nobody), and absent when there are none.
 *
 *  `uninvoiced` (N-04) follows the request's range and side, is served under
 *  the same withheld-rows condition, and is absent when nothing is left out. */
export interface BalancesReport {
  items: CounterpartyBalance[]
  unallocatedOutbound?: UnallocatedOutbound
  uninvoiced?: UninvoicedReceivable
}

export function useBalances(range: string) {
  return useQuery<BalancesReport>({
    queryKey: queryKeys.finance.balances(range),
    queryFn: async () => {
      if (USE_MOCKS) return { items: MOCK_BALANCES, uninvoiced: MOCK_UNINVOICED }
      const report = await api.get<BalancesReport>('/finance/balances', {
        params: { range },
      })
      return {
        items: report.items,
        unallocatedOutbound: report.unallocatedOutbound,
        uninvoiced: report.uninvoiced,
      }
    },
  })
}

/** The company-wide register of ISSUED invoices and debit notes behind the
 *  Money page's «Документы» (N-30): `GET /finance/issued-documents`, newest
 *  first, capped at 500 with `total` saying how many matched. `range` floors
 *  the document date; `all` is sent as is, like the other ranged reads: the server
 *  reads any value it does not know as every period.
 *
 *  Finance-guarded server-side (`finance.receivables.read`); a drafts-free,
 *  read-only list — the per-case editor stays on `useCaseDocuments`.
 *  Offline it answers `MOCK_ISSUED_DOCUMENTS`, unfiltered by `range`
 *  like the other fixtures. */
export function useIssuedDocuments(range: string, options: { enabled?: boolean } = {}) {
  return useQuery<IssuedDocumentsPage>({
    queryKey: queryKeys.finance.issuedDocuments(range),
    enabled: options.enabled ?? true,
    queryFn: async () => {
      if (USE_MOCKS) {
        return { items: MOCK_ISSUED_DOCUMENTS, total: MOCK_ISSUED_DOCUMENTS.length }
      }
      const page = await api.get<IssuedDocumentsPage>('/finance/issued-documents', {
        params: { range },
      })
      return { items: page.items, total: page.total }
    },
  })
}

/** What `GET /finance/payments` filters by. All are ANDed server-side; an
 *  unknown `status` or `unallocated` is a 422 rather than an empty list. */
interface PaymentRegisterParams {
  range: string
  status?: PaymentStatus
  /** S-12: only OUTBOUND payments with an unallocated remainder (pending or
   *  confirmed) — the server's one shared SQL fragment, never a client filter
   *  over `unallocated.amount`. The only value the server accepts is `out`. */
  unallocated?: 'out'
}

/** One read of the cross-case register, shared by the ranged log and the
 *  confirmation queue so the two cannot ask in different shapes. Offline, the
 *  fixture honours `status` the way the server does — the range it does not,
 *  because the fixture has never been dated against a clock. */
async function fetchPaymentRegister(params: PaymentRegisterParams): Promise<Payment[]> {
  if (USE_MOCKS) {
    return MOCK_PAYMENTS.filter(
      (payment) =>
        (!params.status || payment.status === params.status) &&
        // Offline only: the fixture has no SQL behind it to ask.
        (!params.unallocated ||
          (payment.direction === 'out' && !/^-?0*\.?0*$/.test(payment.unallocated.amount))),
    )
  }
  return (
    await api.get<{ items: Payment[] }>('/finance/payments', {
      params: { range: params.range, status: params.status, unallocated: params.unallocated },
    })
  ).items
}

/** The cross-case payment log the Money page lists (Story 4.5).
 *
 * The real `GET /finance/payments`, Finance/Super-admin guarded server-side —
 * not the fixture rows this hook used to read out of `internal/showcase`.
 *
 * `enabled` lets the payments tab stop asking for the ranged log while it shows
 * the confirmation queue instead (QW-10): the two are different questions, and
 * the queue is the one on screen. */
export function usePayments(
  range: string,
  options: { enabled?: boolean; unallocatedOut?: boolean } = {},
) {
  /* S-12: «Финансы → Сальдо»'s «Показать» lands here with `unallocated=1`,
     which becomes the server's `unallocated=out` — the list then holds exactly
     the payments the balances banner counted. Keyed apart from the plain log,
     under `finance.payments`, so every payment write still invalidates it. */
  const unallocated = options.unallocatedOut ? ('out' as const) : undefined
  return useQuery<Payment[]>({
    queryKey: queryKeys.finance.payments(unallocated ? { range, unallocated } : { range }),
    enabled: options.enabled ?? true,
    queryFn: () => fetchPaymentRegister({ range, unallocated }),
  })
}

/** Whether this reader works the payment-confirmation queue: they may read the
 *  register AND confirm what is in it. The same predicate gates the request
 *  below and the queue's controls on the payments tab, so a reader is never
 *  shown a chip for a query that was never made.
 *
 *  A courtesy, not the control (AD-21): the server refuses the read and the
 *  confirmation on its own. */
export function canWorkPaymentQueue(user: CurrentUser | null | undefined): boolean {
  return canAll(user, CAP.financePaymentConfirm, CAP.financeReceivablesRead)
}

/** Every payment nobody has confirmed yet, from EVERY period (QW-10, F-1).
 *
 * One query behind three surfaces — the sidebar's Finance badge, the count on
 * the payments tab's chip, and the queue the chip opens — so the three cannot
 * disagree about how many there are. All-time on purpose: a payment booked last
 * quarter and never checked is the one a ranged count would hide, and the one
 * this queue exists to surface.
 *
 * Read only for a reader who may confirm (`canWorkPaymentQueue`); anybody else
 * issues no request at all, because AppShell mounts this on every page.
 *
 * Keyed under `finance.payments` and therefore under `finance.all`, which every
 * payment write — record, allocate, confirm, reject, amend, pay a carrier —
 * invalidates: the badge moves the moment a decision lands. */
export function usePendingPayments() {
  const { data: session } = useSession()
  return useQuery<Payment[]>({
    queryKey: queryKeys.finance.payments({ status: 'pending' }),
    enabled: canWorkPaymentQueue(session),
    queryFn: () => fetchPaymentRegister({ range: 'all', status: 'pending' }),
  })
}

/** The payments on ONE case, each with its allocations and its remaining
 *  unallocated balance.
 *
 *  Under the FINANCE key rather than a case-detail one, for the reason
 *  `useCaseDocuments` gives: recording or allocating already invalidates
 *  `finance.all`, and a second key under `cases` would be a second cache of the
 *  same rows that nothing invalidates — a case section quietly showing a
 *  payment as unallocated after somebody allocated it from the Money page. */
export function useCasePayments(caseId: string) {
  return useQuery<Payment[]>({
    queryKey: queryKeys.finance.payments({ caseId }),
    // An empty id is a caller that resolved no case (or gated the read off),
    // not a case with no payments — useCaseTranches' rule, applied here the
    // day a caller started passing one.
    enabled: caseId !== '',
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_PAYMENTS.filter((payment) => payment.caseId === caseId)
        : (await api.get<{ items: Payment[] }>(`/cases/${caseId}/payments`)).items,
  })
}

/** The expected receivable tranches on ONE case (FR-32, FR-33, Story 4.3).
 *
 * What the allocation sheet picks from. Until `finance.Service.WithTrancheAllocations`
 * registered a target source there was nothing on the server that could resolve
 * an allocation target at all, which is why that sheet took the identifier by
 * hand; a tranche is now the one kind that resolves, so it is the one kind the
 * sheet offers.
 *
 * UNDER THE FINANCE KEY, not a case-detail one, and that is the load-bearing
 * choice here. A tranche's remaining balance moves when an allocation lands, and
 * allocating invalidates `finance.all` — so this list refreshes with it. Read off
 * the composed case detail instead (`CaseMoney.tranches`) and the picker would
 * keep offering a tranche somebody had just settled, because nothing invalidates
 * the case-detail cache on an allocation.
 *
 * THE KEY IS COMPOSED HERE rather than added to `queryKeys.finance`, because
 * `lib/query.ts` belongs to no story in this pass. It is built FROM `finance.all`
 * so the invalidation above reaches it; when somebody owns that file this should
 * move into the registry beside `payments`, which is the same shape asked about a
 * different resource. */
export function useCaseTranches(caseId: string) {
  return useQuery<CaseTrancheRow[]>({
    queryKey: [...queryKeys.finance.all, 'tranches', { caseId }],
    // A payment always belongs to a case (`Payment.caseId`), so an empty id here
    // is a caller that has not resolved one yet rather than a case with none.
    enabled: caseId !== '',
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_TRANCHES.filter((tranche) => tranche.caseId === caseId).map(
            ({ caseId: _caseId, ...tranche }) => tranche,
          )
        : (
            await api.get<{ items: CaseTrancheRow[] }>(`/cases/${caseId}/tranches`)
          ).items,
  })
}

/** The company's OWN legal entities, for the "which of ours received it" field.
 *
 * Session-scoped (`/settings/entities`), deliberately not the Super-admin
 * `/admin/entities` the settings screen reads: that payload carries bank
 * details, which the server serves to that screen and to no other (AD-6), and
 * a Finance user is refused it outright. A picker that 403s is a field nobody
 * can fill. Only the id and the name are read here — everything else on the
 * entity is somebody else's screen. */
export function useIssuingEntityOptions({ enabled }: { enabled?: boolean } = {}) {
  return useQuery<{ id: string; name: string }[]>({
    queryKey: queryKeys.issuingEntities(),
    // For a reader that needs the names only while something is open — the
    // quote's conversion confirmation (QW-11) — rather than on every visit.
    enabled,
    queryFn: async () => {
      const items = USE_MOCKS
        ? MOCK_ENTITIES
        : (await api.get<{ items: { id: string; name: string }[] }>('/settings/entities'))
            .items
      return items.map((entity) => ({ id: entity.id, name: entity.name }))
    },
  })
}

/** The sheet's draft as the server takes it.
 *
 * The amount stays a decimal STRING all the way through (AD-9) — normalised
 * only for the separators a Russian keyboard produces, never through Number().
 * The `1 234,56` a typist enters and the `1234.56` the server stores are the
 * same sum; parsing to a float to get between them is how the cent goes.
 *
 * Nothing about the BOOKING is sent: no applied rate, no USD figure. The server
 * books at the case's registration-date basis out of the FX cache and refuses
 * loudly when the rate for that date is missing (AD-20) — a rate offered from
 * here would be a second source for a number that must have exactly one.
 *
 * The party leaves as exactly ONE shape (S-22d2, `partyPayload`): a directory
 * counterparty's id, the case client's id, or a typed payer's name with no id. */
export function toPaymentPayload(draft: PaymentDraft): PaymentPayload {
  return {
    caseId: draft.caseId,
    reference: draft.reference.trim(),
    ...partyPayload(draft.party),
    direction: draft.direction,
    method: draft.method,
    issuingEntityId: draft.issuingEntityId,
    amount: {
      amount: normaliseDecimal(draft.amount),
      currency: draft.currency,
    },
    /* The transfer fee is OMITTED when nothing was typed, never sent as a zero.
     * Absent means "no fee was recorded"; a 0.00 would read on a report as a
     * transfer that cost the company nothing, which is a claim nobody made —
     * and the server refuses a stored zero outright (migration 0058).
     *
     * The currency falls back to the payment's, because a blank one in the
     * draft MEANS "the payment's own" — that is what the sheet's select renders
     * too, so what was on screen is what is sent. Both halves travel together or
     * neither does; the server refuses a half-answered pair by field. */
    ...(draft.feeAmount.trim() === ''
      ? {}
      : {
          fee: {
            amount: normaliseDecimal(draft.feeAmount),
            currency: draft.feeCurrency === '' ? draft.currency : draft.feeCurrency,
          },
        }),
    receivedOn: draft.receivedOn,
  }
}

/** Books one payment against the log (AD-11: it lands unallocated).
 *
 * Not optimistic. A payment is money, so UX-DR-24's confirmed-write rule
 * applies squarely: the row appears once the server has booked it, never
 * before. The sheet stays open on `pending` and stays open holding the draft
 * if the server refuses, so nothing typed is lost to a failed save. */
export function useRecordPayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, Error, PaymentPayload>({
    mutationFn: async (payload) => {
      if (USE_MOCKS) {
        // The fixture array is module state, so unshifting the row is what
        // makes the sheet behave offline the way the endpoint does. The
        // booking is invented rather than left blank: offline there is no FX
        // cache to ask, and a 1:1 rate on a non-USD payment is visibly a
        // fixture rather than a plausible wrong number.
        const created: Payment = {
          id: `local-pay-${MOCK_PAYMENTS.length + 1}`,
          ...payload,
          issuingEntityName:
            MOCK_ENTITIES.find((entity) => entity.id === payload.issuingEntityId)?.name ??
            '',
          appliedRate: '1.0000',
          rateDate: payload.receivedOn,
          usd: payload.amount.amount,
          allocated: { amount: '0.00', currency: payload.amount.currency },
          unallocated: payload.amount,
          allocations: [],
          /* `POST /finance/payments` is the FINANCE route (`finance.payment.record`),
             and a payment recorded by the role that confirms is born confirmed
             (0059, L3). Offline there is no caller to ask, so the fixture states
             the answer that route always gives. */
          status: 'confirmed',
          /* And the verdicts that route's holder is served on a confirmed row
             (S-21): allocate it, reverse what it settled when it is money
             received; a confirmed record is amended and deleted by nobody. */
          canAllocate: true,
          canReverseAllocations: payload.direction === 'in',
          canAmend: false,
          canDelete: false,
        }
        MOCK_PAYMENTS.unshift(created)
        return created
      }
      return api.post<Payment>('/finance/payments', payload)
    },
    onSuccess: (payment) => {
      // The whole finance surface moves when cash lands — balances and aging
      // are computed from the same payments this row joins, so refreshing the
      // log alone would leave the other tabs quietly disagreeing with it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      // And the CASE, which is a different tree entirely — see `invalidateCase`.
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

/** Records a client's payment on ONE case and, in the same act, what it
 *  settles (S-21): `POST /cases/:id/payments` with its optional `splits` and
 *  `clientKey`.
 *
 *  One request, one transaction server-side — the payment, every split, their
 *  audit and the pending notice — so a split over its document refuses the
 *  whole request and leaves no payment behind. The `clientKey` the sheet
 *  generated when it opened makes a retry after a timeout safe: the same
 *  payment fields answer `200` with the payment the first attempt recorded.
 *
 *  Not optimistic, for `useRecordPayment`'s and `useAllocatePayment`'s reasons
 *  at once. The invalidation is theirs together: the finance tree (the case's
 *  payments and its receivables hang under it), the case (its money block),
 *  the dashboard when something was allocated, and «Мои задачи» when the
 *  payment was born pending — a Sales record waits in Finance's
 *  `payments-to-confirm`, and the strip above the dashboard counts it. */
export function useRecordCasePayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, Error, { caseId: string; payload: CasePaymentPayload }>({
    mutationFn: async ({ caseId, payload }) => {
      if (USE_MOCKS) {
        // Offline there is no ledger to split against: the payment lands as
        // the Finance route would book it, and the splits are dropped rather
        // than dressed up as allocations nothing resolved.
        const created: Payment = {
          id: `local-pay-${MOCK_PAYMENTS.length + 1}`,
          caseId,
          reference: payload.reference,
          counterpartyName: payload.counterpartyName,
          ...(payload.clientId === undefined ? {} : { clientId: payload.clientId }),
          direction: 'in',
          method: payload.method,
          issuingEntityId: payload.issuingEntityId,
          issuingEntityName:
            MOCK_ENTITIES.find((entity) => entity.id === payload.issuingEntityId)?.name ?? '',
          amount: payload.amount,
          appliedRate: '1.0000',
          rateDate: payload.receivedOn,
          usd: payload.amount.amount,
          receivedOn: payload.receivedOn,
          allocated: { amount: '0.00', currency: payload.amount.currency },
          unallocated: payload.amount,
          allocations: [],
          status: 'confirmed',
          canAllocate: true,
          canReverseAllocations: true,
          canAmend: false,
          canDelete: false,
        }
        MOCK_PAYMENTS.unshift(created)
        return created
      }
      return api.post<Payment>(`/cases/${caseId}/payments`, payload)
    },
    onSuccess: (payment, { payload }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateCase(queryClient, payment.caseId)
      if ((payload.splits?.length ?? 0) > 0) invalidateDashboard(queryClient)
      if (payment.status === 'pending') invalidateTasks(queryClient)
    },
  })
}

/** Withdraws ONE receivable allocation of an inbound payment, with a reason
 *  (S-21): `POST /payments/:id/allocations/:allocationId/reverse`.
 *
 *  The reason is the record — the row stops existing, and the audit entry
 *  carrying it is the only place the fact survives — so it is required here as
 *  it is server-side (422 `reason: required`). The answer is the whole payment
 *  as it now stands, for `useAllocatePayment`'s reason: `allocated` and
 *  `unallocated` moved and must not be recomputed here.
 *
 *  One request per allocation: the dialog that reverses several sends them one
 *  after another and stops at the first refusal. Invalidates as an allocation
 *  does — the finance tree, the dashboard and the case. */
export function useReverseAllocation() {
  const queryClient = useQueryClient()
  return useMutation<
    Payment,
    Error,
    { paymentId: string; allocationId: string; reason: string }
  >({
    mutationFn: async ({ paymentId, allocationId, reason }) => {
      if (USE_MOCKS) {
        const payment = MOCK_PAYMENTS.find((row) => row.id === paymentId)
        if (payment === undefined) throw new Error(`no payment ${paymentId}`)
        payment.allocations = payment.allocations.filter((row) => row.id !== allocationId)
        // Offline only: the fixture has no ledger to re-derive the two from.
        const allocated = payment.allocations.reduce(
          (sum, row) => sum + Number(row.amount.amount),
          0,
        )
        payment.allocated = { amount: allocated.toFixed(2), currency: payment.amount.currency }
        payment.unallocated = {
          amount: (Number(payment.amount.amount) - allocated).toFixed(2),
          currency: payment.amount.currency,
        }
        return payment
      }
      return (
        await api.post<{ payment: Payment }>(
          `/payments/${paymentId}/allocations/${allocationId}/reverse`,
          { reason },
        )
      ).payment
    },
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateDashboard(queryClient)
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

/** The allocation sheet's rows as the server takes them.
 *
 * Every split travels in ONE request. The server takes each ceiling check under
 * `FOR UPDATE` inside a single transaction and commits only if all of them pass
 * (AD-11); sending three requests would let two land and the third be refused,
 * leaving a payment half-applied to paper the user meant to settle together.
 *
 * The row's `key` is dropped here — it is React's, never the wire's — and the
 * amount stays a decimal string, normalised for a comma keyboard exactly as a
 * payment's is. */
export function toAllocationPayload(
  splits: AllocationSplitDraft[],
  currency: string,
): AllocationPayload {
  return {
    splits: splits.map((split) => ({
      targetKind: split.targetKind,
      targetId: split.targetId.trim(),
      amount: { amount: normaliseDecimal(split.amount), currency },
    })),
  }
}

/** Applies a payment to the documents it settles (AD-11).
 *
 * Emphatically NOT optimistic, and this is the epic's own example of where
 * UX-DR-24 bites hardest. The conservation law — Σ splits ≤ the payment,
 * Σ allocations ≤ each target's total — can only be evaluated by the server,
 * under row locks, against sums another Finance user may be changing in the
 * same instant. A row written into the cache before that answer comes back
 * would be a receivable the client shows as settled and the ledger does not,
 * and the refusal that follows would arrive after the screen had already said
 * yes. So nothing is written here: success invalidates and the confirmed rows
 * arrive from the server that accepted them.
 *
 * The response carries the whole PAYMENT rather than the new allocation rows,
 * because its `allocated` and `unallocated` figures move with them and a client
 * that recomputed the split itself would be doing money arithmetic (AD-9). */
export function useAllocatePayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, Error, { paymentId: string; payload: AllocationPayload }>({
    mutationFn: async ({ paymentId, payload }) => {
      if (USE_MOCKS) {
        const payment = MOCK_PAYMENTS.find((row) => row.id === paymentId)
        if (payment === undefined) throw new Error(`no payment ${paymentId}`)

        /* Offline there is no target registry to resolve a label against, so
           the id is echoed with its kind rather than dressed up as "tranche 2
           of 2" — a fixture label that looks resolved would suggest a lookup
           this build cannot do. The FX settlement difference is zero for the
           same reason: it is the gap between two booked rates, and offline
           there is only one. */
        const created: Allocation[] = payload.splits.map((split, index) => ({
          id: `local-alloc-${payment.allocations.length + index + 1}`,
          paymentId,
          targetKind: split.targetKind,
          targetId: split.targetId,
          targetLabel: `${split.targetKind} ${split.targetId}`,
          amount: split.amount,
          appliedRate: payment.appliedRate,
          rateDate: payment.rateDate,
          usd: split.amount.amount,
          fxSettlementDifference: '0.0000',
          allocatedAt: new Date().toISOString(),
        }))

        // The two figures are recomputed together from the rows, so the fixture
        // cannot drift into showing a split that does not add up.
        payment.allocations = [...payment.allocations, ...created]
        const allocated = payment.allocations.reduce(
          (sum, row) => sum + Number(row.amount.amount),
          0,
        )
        payment.allocated = {
          amount: allocated.toFixed(2),
          currency: payment.amount.currency,
        }
        payment.unallocated = {
          amount: (Number(payment.amount.amount) - allocated).toFixed(2),
          currency: payment.amount.currency,
        }
        return payment
      }

      return (
        await api.post<{ payment: Payment }>(`/payments/${paymentId}/allocations`, payload)
      ).payment
    },
    onSuccess: (payment) => {
      /* Same reach as recording a payment: an allocation is what moves a
         receivable, so balances and aging are answering a different question
         the moment it lands. Invalidating the payment keys alone would leave
         the other tabs disagreeing with the row the user just created. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      // And the dashboard's payables, which are net of the allocations.
      invalidateDashboard(queryClient)
      /* The case id comes off the ANSWER rather than off the variables, which
         carry a payment id and a list of splits and no case at all. The
         response is the whole payment for its own reason (see above), and it
         names the case the money moved against. */
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

/* ── Paying a carrier: one act, one request ──────────────────────────────────
 *
 * Three acts used to be three requests from two sheets: record the actual cost
 * line (because a plan is not a debt and nothing can be allocated to it),
 * record the payment, allocate the payment to the line. Each could fail after
 * the one before it had landed, which for money is worse than a refusal — a
 * booked payment with no allocation, or an actual cost line recognised against
 * an invoice that never arrived.
 *
 * `POST /cases/:id/legs/:legId/carrier-payment` does all three in ONE
 * transaction (spec L5). Everything here is therefore a single mutation with a
 * single refusal, and the client states no figure the server derives: no
 * allocation, no booking, no status. The status in particular is the SERVER's
 * (L3) — it follows the recorder's capability, and a client that named one
 * would be granting itself the confirmation it is supposed to be waiting for.
 */

/** The debt one item recognises, when its target has no actual cost line yet.
 *
 *  `planLineId` names the PLANNED line to realise — the agreed rate — and the
 *  server refuses one that is not a planned line of this case and leg
 *  (`ErrPlanNotOnLeg`) or that already carries an actual
 *  (`ErrPlanAlreadyRealised`). With a plan, `unitId` and `wholeLeg` are
 *  ignored: the plan's own unit (or none, for a leg-level plan) is the
 *  attribution, and the client sends neither.
 *
 *  Without a plan the line is created unplanned and EXACTLY ONE of `unitId`
 *  and `wholeLeg: true` says what it is for (S-18a) — a unit of any kind
 *  (S-18b), or the leg as a whole. Neither is 422
 *  `items.<i>.debt.unitId: required`, both is `exclusive`; the retired
 *  `vehicleId` is 422 `items.<i>.debt.vehicleId: retired`.
 *
 *  The debt keeps its OWN currency (D1); `comment` lands in `remarks`
 *  (AD-6-suppressed) and is REQUIRED when the amount exceeds the plan (L2). */
export interface CarrierDebtDraft {
  planLineId?: string
  unitId?: string
  wholeLeg?: true
  amount: string
  currency: string
  comment?: string
}

/** One thing the payment pays (S-18a): an existing ACTUAL line it settles, or
 *  a debt it recognises in the same request — exactly one of the two (422
 *  `items.<i>.expenseLineId: exclusive`). `amount` is the allocation, a
 *  decimal string in the PAYMENT's currency; the item amounts sum to the
 *  payment amount or nothing is written (`items: sum-mismatch`). */
export type CarrierPaymentItemDraft =
  | { amount: string; expenseLineId: string; debt?: never }
  | { amount: string; debt: CarrierDebtDraft; expenseLineId?: never }

/** The whole act, as `POST /cases/:id/legs/:legId/carrier-payment` takes it
 *  since S-18a: one payment over 1..101 items (every unit of a full leg plus
 *  «Плечо целиком», S-18b).
 *
 *  `clientKey` makes the act idempotent: generated once when the sheet opens
 *  and sent with every press of that open, so a retry after a timeout answers
 *  `200` with the payment the first press recorded instead of recording a
 *  second one. Blank or longer than 64 is refused (`clientKey`).
 *
 *  The payment carries no `caseId` and no `direction`: both are the server's
 *  from the path and from the act. «Оплатить перевозчику» is outbound by
 *  construction, and a body that could say otherwise is a body that can book
 *  the client's money as a carrier payment. When the items are more than one,
 *  every target must be ONE directory carrier's (`items: carrier-mismatch`) —
 *  the sheet offers only such a set.
 *
 *  The payee is a DIRECTORY counterparty since S-22d: `payment.counterpartyId`
 *  is this endpoint's own field — required here, where `PaymentPayload`'s is
 *  optional (S-22d2), and the server reads the outer one only. `clientId` is
 *  not this endpoint's: a carrier is never the case's client. Absent → 422
 *  `payment.counterpartyId: required`; malformed or unknown → `invalid`. The
 *  server writes the directory's name and id into the payment;
 *  `counterpartyName` is still sent beside it. A debt whose carrier is typed
 *  only is refused per item (`items.<i>.debt: carrier-not-in-directory`). */
export interface CarrierPaymentPayload {
  clientKey: string
  payment: Omit<PaymentPayload, 'caseId' | 'direction' | 'clientId' | 'counterpartyId'> & {
    counterpartyId: string
  }
  items: CarrierPaymentItemDraft[]
}

/** What the endpoint answers, `201` for a new payment and `200` with the same
 *  envelope for a replayed `clientKey`: the payment as booked, and the ACTUAL
 *  cost line each item settled — the one that already existed, or the one
 *  just recognised. In item order on a `201`; in allocation order on a
 *  replay, which is not guaranteed to match the request. */
export interface CarrierPaymentResult {
  payment: Payment
  expenseLines: ExpenseLine[]
}

/** Pays a carrier: recognise each debt, record ONE payment, allocate it to
 *  every item (L5, S-18a).
 *
 *  Not optimistic, for every reason `useRecordPayment` and `useAllocatePayment`
 *  give at once, and one more of its own: the allocation ceiling is checked
 *  server-side under row locks, and its refusal rolls back the cost line and the
 *  payment too. A row drawn here would have to be un-drawn three ways.
 *
 *  The invalidation is the WIDEST in this file, and deliberately so — this one
 *  request moves a cost line, a payment and an allocation:
 *
 *    · `finance.all` reaches the cross-case payment log, the balances, the aging
 *      and the case's own payment list (which hangs under `finance.payments`);
 *    · `invalidateCase` reaches the case detail, its money block and — by prefix
 *      — `cases.leg(caseId, legId)`, the composed read the direction page's own
 *      four tiles come from. That last one is the point: the desk presses this
 *      button ON the direction page, and «Оплачено» has to move under them. */
export function usePayCarrier() {
  const queryClient = useQueryClient()
  return useMutation<
    CarrierPaymentResult,
    Error,
    { caseId: string; legId: string; payload: CarrierPaymentPayload }
  >({
    mutationFn: ({ caseId, legId, payload }) =>
      api.post<CarrierPaymentResult>(
        `/cases/${caseId}/legs/${legId}/carrier-payment`,
        payload,
      ),
    onSuccess: (_result, { caseId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateDashboard(queryClient)
      invalidateCase(queryClient, caseId)
    },
  })
}

/** Confirms one recorded payment — Finance's second pair of eyes (L3, L7).
 *
 *  Idempotent server-side: confirming an already-confirmed payment answers the
 *  row as it stands rather than moving `confirmedAt`, so a double press tells
 *  the reader the first press worked instead of rewriting when it happened.
 *
 *  The case comes off the ANSWER, like an allocation's: the request carries a
 *  payment id and nothing else, and the response is the whole payment because
 *  its status and its confirmation timestamp both moved.
 *
 *  It moves no dashboard figure (L4: status is in no sum). The count it does
 *  move — the queue's — is «Мои задачи»'s `payments-to-confirm` section since
 *  S-2b.2, so it invalidates that tree too (`invalidateTasks`): the strip
 *  above the dashboard is on screen while the desk confirms, and a re-read on
 *  the next mount left its count standing beside the queue it was just
 *  worked from. The dashboard invalidation stays with the other payment
 *  writes' (`invalidateDashboard`). */
export function useConfirmPayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => api.post<Payment>(`/payments/${paymentId}/confirm`),
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateDashboard(queryClient)
      invalidateTasks(queryClient)
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

/** Deletes a PENDING payment, with its allocations and a stated reason (L8).
 *
 *  This is what «отклонить» means here: there is no rejected status, because a
 *  rejected row that stayed on the ledger is a figure every sum then has to
 *  remember to exclude. The reason is mandatory and goes into the audit trail —
 *  the row is gone, so the trail is the only place left that can say why.
 *
 *  THE RECOGNISED DEBT SURVIVES. Deleting the payment removes the payment and
 *  its allocations; the actual cost line stays, because the carrier is still
 *  owed the money and the record of the payment was the thing that was wrong.
 *
 *  The case id travels in the VARIABLES rather than off the answer, unlike the
 *  confirm above: a 204 carries nothing to read it from, and the case still has
 *  to be refreshed — a direction page holding «Оплачено» from before the
 *  deletion is the same stale figure by a different route. */
export function useDeletePayment() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { paymentId: string; caseId: string; reason: string }>({
    mutationFn: ({ paymentId, reason }) =>
      api.delete<void>(`/payments/${paymentId}`, { body: { reason } }),
    onSuccess: (_void, { caseId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateDashboard(queryClient)
      // A deleted pending payment leaves the `payments-to-confirm` count.
      invalidateTasks(queryClient)
      invalidateCase(queryClient, caseId)
    },
  })
}

/** What a PENDING payment may be corrected to — the amendable fields only.
 *
 *  NO caseId, direction, receivedOn or currency: the last two are the facts
 *  every booking on the row and its allocations hangs off, so the wire not
 *  carrying them is the client half of the enforcement (the server's
 *  `PaymentAmendment` is the other). `amount` is therefore a bare decimal
 *  string — a figure with no currency beside it cannot restate the one the
 *  row already holds. The fee keeps its pair, because its currency IS
 *  amendable.
 *
 *  The party (S-22d2) is the create's three fields, all optional, decided by
 *  the row's direction server-side: an echoed stored id keeps its stored name;
 *  a new counterparty id (outbound only) takes the directory's; a new client
 *  id must be the case's CURRENT client; no id is a typed payer inbound, and
 *  outbound only an exact echo of a row typed before the link existed. A
 *  reader whose party is withheld sends NONE of the three — the server keeps
 *  the stored party for a caller without `counterparty.read` whatever arrives. */
export interface PaymentAmendPayload {
  reference: string
  counterpartyName?: string
  counterpartyId?: string
  clientId?: string
  method: PaymentMethod
  issuingEntityId: string
  amount: string
  fee?: { amount: string; currency: string }
}

/** Corrects a PENDING payment in place (the third fate beside confirm and
 *  reject). A confirmed payment is refused server-side — the way to change a
 *  checked fact is a correcting entry, not an UPDATE. */
export function useAmendPayment() {
  const queryClient = useQueryClient()
  return useMutation<
    Payment,
    Error,
    { paymentId: string; caseId: string; payload: PaymentAmendPayload }
  >({
    mutationFn: ({ paymentId, payload }) =>
      api.put<Payment>(`/payments/${paymentId}`, payload),
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateDashboard(queryClient)
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

export function useApprovals() {
  return useQuery<ApprovalRequest[]>({
    queryKey: queryKeys.finance.approvals(),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_APPROVALS
        : (await api.get<{ items: ApprovalRequest[] }>('/finance/approvals')).items,
  })
}

/** Approves or rejects one pending rate-change request (FR-36). The decided
 *  request leaves the pending list, so success is an invalidation, not a merge.
 *
 *  `caseId` travels with the decision and is not read by the request: it is
 *  there so the CASE can be refreshed beside the queue. An approved rate change
 *  is the agreed rate the case's money is judged against (AD-10), so a decision
 *  taken on the Money page and a case screen open in another tab would otherwise
 *  disagree about the ceiling until something else happened to refetch. */
export function useDecideApproval() {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    { id: string; caseId: string; decision: 'approve' | 'reject' }
  >({
    mutationFn: async ({ id, decision }) => {
      if (USE_MOCKS) {
        // The fixture array is module state, so dropping the row is what makes
        // the button behave offline the way the showcase endpoint does.
        const index = MOCK_APPROVALS.findIndex((approval) => approval.id === id)
        if (index !== -1) MOCK_APPROVALS.splice(index, 1)
        return
      }
      await api.post<void>(`/finance/approvals/${id}/decision`, { decision })
    },
    onSuccess: (_void, { caseId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.approvals(),
      })
      // The decided request leaves «Мои задачи»'s `rate-approvals` section.
      invalidateTasks(queryClient)
      invalidateCase(queryClient, caseId)
    },
  })
}
/* ── The Documents surface ───────────────────────────────────────────────────
 *
 * `useFinanceDocuments`, `useCaseDocuments(caseNumber)`, `useRaiseDocument` as
 * it was and `toDocumentPayload` are all GONE. Every one of them spoke to
 * `/api/finance/documents`, which is `internal/showcase` — a fixture held in the
 * server process's memory. A document raised through them was stored nowhere and
 * could not be found again, and the archive that has existed for some time was
 * called from no file in this client (issue #27).
 *
 * What replaces them reads the REAL paper: client invoices from
 * `/cases/:id/invoices` and debit notes from `/cases/:id/debit-notes`, merged
 * into one `CaseDocument[]` on this side. The two reads live in `useInvoices.ts`
 * and `useDebitNotes.ts` and are CALLED here rather than re-implemented, which
 * is the load-bearing part: their keys hang off `queryKeys.cases.detail(caseId)`
 * and their write mutations seed them, so a second fetch of the same rows under
 * a `finance` key would be a second cache that nothing invalidates — the case
 * section quietly showing a draft somebody issued from the Money page.
 *
 * ── The three stages, and which hook is which (00-decisions §1) ─────────────
 *
 * A document is DRAFT, then RENDERED, then ISSUED, and the three are three
 * separate server calls because they are three separate decisions:
 *
 *   raise    `useRaiseDocument`      opens the draft
 *   render   `useRenderDocument`     archives the PDF — the bytes that will be sent
 *   issue    `useIssueDocument`      mints the number and makes it binding
 *
 * Generating is not issuing (UX-DR-15), and the temptation to collapse them into
 * one call with a flag is exactly what `document.Handlers.Issue`'s comment
 * refuses: a flag is something a client sets by accident.
 *
 * ── `documentId` is the whole state machine ─────────────────────────────────
 *
 * There is ONE signal for "is the paper on screen current", and it is whether
 * the record carries a `documentId`. The server clears that column on EVERY
 * draft write — `TouchInvoice`, `UpdateInvoiceHeader`, `TouchDebitNote`,
 * `UpdateDebitNote` all set it to NULL — so:
 *
 *   documentId present  →  a CURRENT render exists; Issue is available
 *   documentId absent   →  there is no current render; Issue is refused
 *
 * That single field answers both of this screen's awkward questions: Issue is
 * unavailable without a render, and a document amended after being rendered
 * shows the live draft again rather than yesterday's PDF beside today's figures.
 * Do NOT invent a second signal — a client-side "dirty since render" flag would
 * be a second answer to a question the server already answers, and the two
 * disagree the first time somebody edits the note in another tab. The server
 * refuses the issue regardless (`ErrInvoiceNotRendered`); this is the
 * affordance, not the guard.
 */

/** The paper raised against ONE case — its invoices and its debit notes.
 *
 *  Takes the case ID, not the case number. The fixture keyed on the number
 *  because it had nothing better: it answered the whole archive and filtered it
 *  client-side on the one field a document carried. The real reads are nested
 *  under the case's id, which is also the id the row scope is checked against.
 *
 *  TWO queries rather than one, because there is no cross-case archive list to
 *  ask — `GET /api/documents` REQUIRES a source, and the two documents live in
 *  two tables with two different anonymity boundaries. They are the two hooks
 *  the editors already use, called here so that both surfaces share one cache
 *  entry per resource. Merging is this hook's only job.
 *
 *  The composite it returns mimics a `useQuery` result closely enough for the
 *  section's existing loading / error / empty branches — `data`, `isPending`,
 *  `isError`, `error`, `refetch` — because the alternative is every consumer
 *  learning to combine two query states, which is how one of them ends up
 *  rendering an empty list while the other half is still in flight.
 *
 *  `isPending` is either being pending and `isError` is either failing, both
 *  deliberately pessimistic: a section that showed the invoices while the debit
 *  notes were still loading would announce "3 documents" and then quietly become
 *  5, and one that showed them while the debit-note read had FAILED would state
 *  that a case has no debit notes when the truth is that nobody could ask.
 *
 *  ── Either half can be switched off ─────────────────────────────────────────
 *
 *  The two reads are guarded by two different capabilities —
 *  `document.invoice.render` and `document.debitnote.render` — and a role may
 *  hold one without the other. Asking for the half the reader may not see is a
 *  guaranteed 403, and the pessimistic `isError` above would turn it into an
 *  error card over the half they CAN see. So the case section says which halves
 *  to read, and both default on so the Money page's call is unchanged.
 *
 *  A switched-off half contributes no rows, is never pending and never failing:
 *  it is a question nobody asked, not an empty answer and not a missing one. It
 *  is also LEFT OUT of `refetch`, and that is the part that is easy to get
 *  wrong — TanStack's `refetch()` fetches a disabled query regardless, and the
 *  disabled half's key carries an empty case id, so a retry would ask
 *  `/cases//invoices`. */
export function useCaseDocuments(
  caseId: string,
  {
    invoices: readInvoices = true,
    debitNotes: readDebitNotes = true,
  }: { invoices?: boolean; debitNotes?: boolean } = {},
) {
  const invoices = useCaseInvoices(readInvoices ? caseId : '')
  const debitNotes = useCaseDebitNotes(readDebitNotes ? caseId : '')

  const invoiceRows = readInvoices ? invoices.data : NO_INVOICES
  const debitNoteRows = readDebitNotes ? debitNotes.data : NO_DEBIT_NOTES

  const data = useMemo(() => {
    if (invoiceRows === undefined || debitNoteRows === undefined) return undefined
    return [
      ...invoiceRows.map(toCaseDocumentFromInvoice),
      ...debitNoteRows.map(toCaseDocumentFromDebitNote),
    ].sort(compareNewestFirst)
  }, [invoiceRows, debitNoteRows])

  return {
    data,
    isPending:
      (readInvoices && invoices.isPending) || (readDebitNotes && debitNotes.isPending),
    isError: (readInvoices && invoices.isError) || (readDebitNotes && debitNotes.isError),
    /* The invoice read's failure wins when both fail, arbitrarily but
       consistently — one message on screen rather than two, and the same one
       every time so a person reporting it describes something reproducible. */
    error: (readInvoices ? invoices.error : null) ?? (readDebitNotes ? debitNotes.error : null),
    refetch: () =>
      Promise.all([
        ...(readInvoices ? [invoices.refetch()] : []),
        ...(readDebitNotes ? [debitNotes.refetch()] : []),
      ]),
  }
}

/* Stable empties for a switched-off half, so the merge above is memoised on
   references that do not change every render. */
const NO_INVOICES: InvoiceSummary[] = []
const NO_DEBIT_NOTES: DebitNote[] = []

/** Newest first, by the date the document states about ITSELF.
 *
 *  The invoice date and the note date, not the issue date: a draft has no issue
 *  date at all, and sorting on one would pile every draft at one end of the list
 *  regardless of when it was written. Both are date-only `YYYY-MM-DD` (AD-19),
 *  which sorts correctly as text — no Date object is constructed here, because
 *  parsing a date-only string into one drags in a timezone that the string
 *  deliberately does not have.
 *
 *  The id breaks the tie. Several documents legitimately share a date — FR-61
 *  invoices a case in tranches — and a comparator that returned 0 for them would
 *  leave their order to whichever list responded first, so the section would
 *  reshuffle itself on a refetch that changed nothing. */
function compareNewestFirst(a: CaseDocument, b: CaseDocument): number {
  if (a.documentDate !== b.documentDate) {
    return a.documentDate < b.documentDate ? 1 : -1
  }
  return a.id < b.id ? 1 : -1
}

/** What raising a document takes, whichever kind it is.
 *
 *  A discriminated union rather than one flattened payload with optional halves,
 *  because the two creates genuinely differ: an invoice sends a HEADER and the
 *  server prefills its lines from the accepted revision's printed charges
 *  (AD-15), while a debit note's charges travel WITH the create because a demand
 *  with no stated reason is a document nobody pays. A single shape with both
 *  sets optional would make "invoice with lines" and "debit note without them"
 *  expressible in the type, and both are refused by the server. */
export type RaiseDocumentPayload =
  | { kind: 'invoice'; caseId: string; invoice: NewInvoicePayload }
  | { kind: 'debit-note'; caseId: string; note: DebitNotePayload }

/** Raises one invoice or debit note against a case. It opens as a DRAFT.
 *
 *  ONE mutation for both screens (00-decisions §4). The case's Documents section
 *  and the Money page raise through the same sheet and the same call; what the
 *  case adds is a filter and a prefill, because the case number, the client and
 *  the issuing entity are facts that page already knows and making somebody
 *  retype them is how an invoice ends up filed against the wrong case.
 *
 *  Not optimistic, and for a sharper reason than a payment's: the FX rate is
 *  resolved server-side against the economic date (AD-20) and the totals are
 *  computed there (AD-9). A row written into the cache ahead of the response
 *  would carry figures this client invented, on a document.
 *
 *  Deliberately NOT seeded into the detail caches the way the editors' writes
 *  are. The response is mapped to the view shape the caller renders, and the
 *  full record it came from would have to be threaded through a discriminated
 *  return to reach `useInvoices`' seed — so the list is invalidated instead and
 *  the new draft arrives from the read that owns it. One extra round trip on the
 *  least frequent action on the screen, against one fewer place a cache can be
 *  written from. */
export function useRaiseDocument() {
  const queryClient = useQueryClient()
  return useMutation<CaseDocument, Error, RaiseDocumentPayload>({
    mutationFn: async (payload) => {
      if (payload.kind === 'invoice') {
        const invoice = await api.post<Invoice>(
          `/cases/${payload.caseId}/invoices`,
          payload.invoice,
        )
        return toCaseDocumentFromInvoice(invoice)
      }
      const note = await api.post<DebitNote>(
        `/cases/${payload.caseId}/debit-notes`,
        payload.note,
      )
      return toCaseDocumentFromDebitNote(note)
    },
    onSuccess: (_document, { caseId }) => invalidateCase(queryClient, caseId),
  })
}

/** Which record a render, an issue or a preview is about.
 *
 *  The kind is carried rather than inferred from the id, because nothing about a
 *  uuid says which table it came from and a client that guessed would post an
 *  invoice's id to the debit-note route and read the 404 as "deleted". */
export interface DocumentRef {
  caseId: string
  kind: 'invoice' | 'debit-note'
  id: string
}

/** Renders the record into the archive — the "Generate" press (FR-63, AD-18).
 *
 *  The server lays out the PDF, puts the bytes in object storage and links the
 *  archive row to the record. THOSE bytes are what the screen shows next and
 *  what the recipient is eventually sent: the preview and the issued artifact
 *  are the same file, which is the sentence `document.Handlers.Preview` exists
 *  to honour. An HTML approximation laid out on this side is a draft and is
 *  labelled one; it is never presentable as the document.
 *
 *  Answers with the whole record, so the fresh `documentId` — the signal every
 *  affordance on this screen reads — arrives with the response rather than a
 *  refetch later. */
export function useRenderDocument() {
  const queryClient = useQueryClient()
  return useMutation<CaseDocument, Error, DocumentRef>({
    mutationFn: (ref) => writeToRecord(ref, 'document'),
    onSuccess: (_document, { caseId }) => invalidateCase(queryClient, caseId),
  })
}

/** Issues the record — the distinct second action (UX-DR-15, AD-12).
 *
 *  Through the RECORD's own route, `POST /cases/:id/invoices/:id/issue` or the
 *  debit-note twin — never `POST /api/documents/:id/issue`. That route exists
 *  and answers 409 for these two kinds, naming the route to use instead: issuing
 *  mints a number from the entity's series and stamps the record inside one
 *  transaction, and the archive knows nothing about either. A caller holding the
 *  invoice capability would otherwise sail through the archive's door into a
 *  half-issue.
 *
 *  Refused, not ignored, on a second press — both here and on the server. The
 *  caller pressed a button twice and needs to be told the FIRST press worked, so
 *  the refusal is surfaced as "this was already issued" rather than swallowed;
 *  told nothing, they press it again. Issuing is irreversible (AD-24), so the
 *  client confirms before calling this. That confirmation is an affordance and
 *  not a guard: the irreversibility is enforced by the server's WHERE clause. */
export function useIssueDocument() {
  const queryClient = useQueryClient()
  return useMutation<CaseDocument, Error, DocumentRef>({
    mutationFn: (ref) => writeToRecord(ref, 'issue'),
    onSuccess: (_document, { caseId }) => {
      invalidateCase(queryClient, caseId)
      /* Issuing is the moment a demand becomes a receivable, and aging and
         balances are computed from exactly those. They are CROSS-CASE figures
         under the finance key, so the case-detail invalidation above cannot
         reach them — and a Money page left holding the old buckets would age a
         document it had just watched become real. Rendering does not do this:
         it writes bytes to storage and moves no ledger figure. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
    },
  })
}

/** POSTs to one of the two records' action routes and maps the answer.
 *
 *  Both routes on both documents answer the WHOLE record, which is what lets the
 *  two mutations above share this: the kind decides the path and the mapper, and
 *  nothing else about them differs. */
function writeToRecord(
  { caseId, kind, id }: DocumentRef,
  action: 'document' | 'issue',
): Promise<CaseDocument> {
  if (kind === 'invoice') {
    return api
      .post<Invoice>(`/cases/${caseId}/invoices/${id}/${action}`)
      .then(toCaseDocumentFromInvoice)
  }
  return api
    .post<DebitNote>(`/cases/${caseId}/debit-notes/${id}/${action}`)
    .then(toCaseDocumentFromDebitNote)
}

/** Refreshes everything one finance write moved on this case.
 *
 *  ONE invalidation, and it reaches further than it looks. Both document lists,
 *  both detail families AND the direction pages are keyed off
 *  `queryKeys.cases.detail(caseId)` — invalidation matches by prefix — so this
 *  one call refreshes the invoices, the debit notes, the case's own money read
 *  and `queryKeys.cases.leg(caseId, legId)` beneath it. That is the point. A
 *  document raised or issued here moves the Money page too, and that is the
 *  reason the Documents section is a VIEW of the archive rather than a second
 *  one: the two surfaces answer different questions about the same rows, and a
 *  section that refreshed only its own list would let them disagree in front of
 *  the reader.
 *
 *  It is called `invalidateCase` rather than `invalidateCaseDocuments` because
 *  the payment writes need it too, and they need it for the sharper version of
 *  the same failure. `queryKeys.finance.all` and `queryKeys.cases.all` are two
 *  separate trees: paying a haulier from a direction and allocating that
 *  payment to a truck's cost line refreshed every finance tab and left
 *  «Оплачено» and «Остаток» on the direction's own page — and on the case's
 *  direction card, which is where the desk reads them — showing the figures
 *  from before the payment (story 6 AC 3). */
function invalidateCase(
  queryClient: ReturnType<typeof useQueryClient>,
  caseId: string,
) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
}

/** Refreshes the founder dashboard after a payment write that moves a
 *  payable.
 *
 *  `queryKeys.dashboard.*` is a THIRD tree beside `finance.*` and `cases.*`, and
 *  none of the payment writes reached it. Payables are net of the outbound
 *  payments allocated to each cost line, so paying a carrier, allocating,
 *  deleting or amending a payment moves the dashboard's payables — and with the
 *  default 30-second staleTime the founder read the figure from before the
 *  write (P0-1).
 *
 *  CONFIRMING still calls it, though since S-2b.2 it has no dashboard figure to
 *  move: it moves no payable (L4: status is in no sum), and the
 *  payment-confirmation row it used to shrink left «Требует решения» for «Мои
 *  задачи»'s `payments-to-confirm` section (the strip above the dashboard),
 *  which `invalidateTasks` reaches. The call is kept as the cheap, safe side:
 *  an invalidation that refetches an unchanged figure costs one read.
 *  Recording an unallocated payment is the one write that does not call this:
 *  it moves no payable (AD-11: a payment lands unallocated). */
function invalidateDashboard(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
}

/** Refreshes «Мои задачи» after a finance write that shrinks one of its
 *  sections — a FOURTH tree (`queryKeys.tasks.*`), beside the three above.
 *
 *  Confirming or deleting a pending payment takes it out of
 *  `payments-to-confirm`; deciding a rate change takes the request out of
 *  `rate-approvals`. The list is re-read on every mount, but the strip that
 *  renders those sections sits on the dashboard, which is where the desk works
 *  from — so without this its counts stood beside the queue that had just
 *  emptied (#77). Other people's acts still arrive by `queriesMovedByTopic`;
 *  this is the reader's own. */
function invalidateTasks(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all })
}

/** A short-lived signed link to an archived artifact — `{url, expiresAt}`. */
export interface DocumentPreview {
  url: string
  expiresAt: string
}

/** The signed URL for one archived document (AD-18, 00-decisions §2).
 *
 *  NEVER CACHED, and every option below says so in a different way. The link is
 *  pre-signed and dies after five minutes, and it dies on purpose: AD-18 gives
 *  it that expiry precisely so a link copied out of a browser is worthless
 *  before it can be shared. A hook that held it for the default 30 seconds of
 *  freshness would be merely wrong; one that held it for an hour would hand a
 *  person a dead link and tell them the document is broken.
 *
 *  So — `staleTime: 0`, which is `useOfferPreview`'s precedent on the quote
 *  screen and taken for a related reason: the user's loop here is render → look
 *  → amend → render, and a cached answer would show them the document they just
 *  replaced. `gcTime: 0` on top of it, which `useOfferPreview` does not need:
 *  the offer preview is structured JSON this client lays out, while this is a
 *  CREDENTIAL, and a credential left sitting in a cache after the viewer closed
 *  is a credential something else can read. It is handed to an <iframe> or an
 *  <object> and never written into an anchor a person could copy out of the
 *  page, and never persisted.
 *
 *  `retry: false`, and this is the one that changes what the screen can say. The
 *  `document` module is NIL-ABLE: on a deployment with no object storage
 *  configured, `main.go` never constructs its service and `routes.go` never
 *  mounts its routes, so this endpoint answers 404 rather than 500. A 404 here
 *  therefore means "the archive is not available on this deployment", NOT "no
 *  paper exists" — the caller must render that as such, because an empty frame
 *  would say there is no document when the truth is that there is no bucket.
 *  Retrying that answer twice would just delay the message by two round trips;
 *  the global retry rule already declines to retry a decision the server has
 *  made, and this states it locally because it is load-bearing here.
 *
 *  `enabled` only with an id, because a record with no current render has no
 *  archive row to ask about and `/documents/undefined/preview` is a 404 that
 *  means something else entirely. The caller's retry after a failed load is an
 *  ordinary `refetch()`: with `staleTime: 0` it goes to the server and comes
 *  back with a URL good for another five minutes, which is AC 5's whole
 *  requirement. */
export function useDocumentPreviewUrl(documentId: string | undefined) {
  return useQuery<DocumentPreview>({
    /* Composed here rather than added to `queryKeys`, the licence
       `useCaseTranches` above takes and for a stronger reason: the registry
       exists so a feature can invalidate a subtree at once, and NOTHING should
       ever invalidate this entry — it is discarded the moment the viewer closes
       it. A key in the registry is an invitation to reach it. */
    queryKey: ['documents', 'preview', documentId] as const,
    enabled: documentId !== undefined && documentId !== '',
    queryFn: () => api.get<DocumentPreview>(`/documents/${documentId}/preview`),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}
